import { useEffect, useState } from 'react';
import { getAllOrganizerEvents, getOrganizerProfile } from '../api';

export interface FreeEventLimit {
  created: number;
  limit: number | null;
  remaining: number | null;
  isLoading: boolean;
  error: string;
}

const getEventCount = (response: any): number => {
  const events = Array.isArray(response)
    ? response
    : Array.isArray(response?.events)
      ? response.events
      : Array.isArray(response?.data)
        ? response.data
        : [];

  return events.length;
};

export const useFreeEventLimit = (enabled: boolean): FreeEventLimit => {
  const [state, setState] = useState<FreeEventLimit>({
    created: 0,
    limit: null,
    remaining: null,
    isLoading: enabled,
    error: '',
  });

  useEffect(() => {
    if (!enabled) {
      setState({ created: 0, limit: null, remaining: null, isLoading: false, error: '' });
      return;
    }

    let isMounted = true;
    const loadLimit = async () => {
      setState((current) => ({ ...current, isLoading: true, error: '' }));

      try {
        const [profile, events] = await Promise.all([
          getOrganizerProfile(),
          getAllOrganizerEvents(),
        ]);
        const limitValue = Number(profile?.availableFreeEvents);
        const limit = Number.isFinite(limitValue) ? Math.max(0, limitValue) : null;
        const created = getEventCount(events);

        if (isMounted) {
          setState({
            created,
            limit,
            remaining: limit === null ? null : Math.max(0, limit - created),
            isLoading: false,
            error: limit === null ? 'Free event limit is not available.' : '',
          });
        }
      } catch (error) {
        console.error('Error loading free event limit:', error);
        if (isMounted) {
          setState((current) => ({
            ...current,
            isLoading: false,
            error: 'Unable to verify your free event limit. Please try again.',
          }));
        }
      }
    };

    loadLimit();
    return () => {
      isMounted = false;
    };
  }, [enabled]);

  return state;
};
